import { Link } from "react-router-dom";
import { useState, useEffect, useRef, useCallback } from "react";
import api from "@/services/api";
import { useTheme } from "@/context/ThemeContext";
import { useTranslation } from "@/context/TranslationContext";
import ParticleCanvas from "@/components/base/ParticleCanvas";
import { useMousePosition } from "@/hooks/useMousePosition";

const LOGO_SRC =
  "https://static.readdy.ai/image/8f57b6c24a127be3ec5c87bfaa3e1cdb/10eec45dd18ea3b56875e869c153a69b.png";

const techBadges = [
  "React","Node.js","PostgreSQL","Stripe","Socket.io",
  "AI/ML","TypeScript","Docker","AWS","Kubernetes",
  "LangChain","Redis","FastAPI","WebSocket","GraphQL",
  "React","Node.js","PostgreSQL","Stripe","Socket.io",
  "AI/ML","TypeScript","Docker","AWS","Kubernetes",
  "LangChain","Redis","FastAPI","WebSocket","GraphQL",
];

const rotatingWords = [
  "SaaS Products","Secure Systems","AI Tools",
  "Fintech Apps","Cyber Shield","Web Platforms",
];

const terminalLines = [
  { cmd: "$ npm run build:prod",       out: "> compiled in 1.2s ✓"      },
  { cmd: "$ docker push registry/app", out: "> digest: sha256:a3f9..."   },
  { cmd: "$ kubectl rollout status",   out: "> deployment: success ✓"   },
  { cmd: "$ ./deploy.sh --env prod",   out: "> all systems nominal ✓"   },
  { cmd: "$ git push origin main",     out: "> pipeline triggered ✓"    },
  { cmd: "$ pm2 restart ecosystem",    out: "> processes restarted ✓"   },
];

interface LiveProject {
  id: string; title: string; description?: string;
  stage: string; progress: number; thumbnail_url?: string;
  client?: { company_name: string };
}

const convertDriveLink = (url: string): string => {
  if (!url) return "";
  const fileMatch = url.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (fileMatch) return `https://drive.google.com/thumbnail?id=${fileMatch[1]}&sz=w800`;
  const idMatch = url.match(/[?&]id=([a-zA-Z0-9_-]+)/);
  if (idMatch) return `https://drive.google.com/thumbnail?id=${idMatch[1]}&sz=w800`;
  return url;
}; 

// ─────────────────────────────────────────────────────────────────────────────
// GHOST TRAIL — soft shadow that follows the mouse.
// Each segment is a single blurred stroke rendered with very low opacity.
// No thick cores, no harsh lines — just a translucent whisper trail.
// Works on both themes by switching between a warm-white and cool-blue shadow.
// ─────────────────────────────────────────────────────────────────────────────
interface TrailPoint { x: number; y: number; born: number; }
const TRAIL_LIFETIME = 1200;  // ms — lingers like a water ripple
const TRAIL_MIN_DIST = 3;     // px — skip micro jitter
const TRAIL_MAX_PTS  = 120;

function useGhostTrail(
  canvasRef: React.RefObject<HTMLCanvasElement>,
  isLight: boolean,
) {
  const ptsRef      = useRef<TrailPoint[]>([]);
  const prevPosRef  = useRef<{ x: number; y: number } | null>(null);
  const rafRef      = useRef<number>(0);

  const resize = useCallback(() => {
    const c = canvasRef.current;
    if (!c) return;
    const { width, height } = c.getBoundingClientRect();
    if (c.width !== width || c.height !== height) { c.width = width; c.height = height; }
  }, [canvasRef]);

  const draw = useCallback(() => {
    const c = canvasRef.current;
    if (!c) { rafRef.current = requestAnimationFrame(draw); return; }
    const ctx = c.getContext("2d");
    if (!ctx) { rafRef.current = requestAnimationFrame(draw); return; }
    resize();
    ctx.clearRect(0, 0, c.width, c.height);

    const now = performance.now();
    ptsRef.current = ptsRef.current.filter(p => now - p.born < TRAIL_LIFETIME);

    const pts = ptsRef.current;
    if (pts.length < 2) { rafRef.current = requestAnimationFrame(draw); return; }

    // Draw a single smooth polyline with age-based opacity falloff.
    // We walk pairs of points and draw short segments, each fading by age.
    for (let i = 1; i < pts.length; i++) {
      const a = pts[i - 1];
      const b = pts[i];
      const age   = (now - b.born) / TRAIL_LIFETIME;          // 0=new → 1=dead
      // Slow cubic fade — lingers long but stays barely visible
      const alpha = Math.pow(1 - age, 3.5) * 0.13;

      // Light: very faint indigo ripple. Dark: very faint white ripple.
      const r  = isLight ? 99  : 255;
      const g  = isLight ? 102 : 255;
      const bl = isLight ? 241 : 255;

      ctx.save();
      ctx.lineCap  = "round";
      ctx.lineJoin = "round";

      // Single wide, heavily blurred layer — no hard edge, pure water shimmer
      ctx.filter      = "blur(18px)";
      ctx.strokeStyle = `rgba(${r},${g},${bl},${alpha.toFixed(4)})`;
      ctx.lineWidth   = 38;
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.stroke();

      ctx.restore();
    }

    rafRef.current = requestAnimationFrame(draw);
  }, [canvasRef, isLight, resize]);

  useEffect(() => {
    rafRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(rafRef.current);
  }, [draw]);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ro = new ResizeObserver(resize);
    ro.observe(c);
    return () => ro.disconnect();
  }, [canvasRef, resize]);

  const onMouseMove = useCallback((x: number, y: number) => {
    const now  = performance.now();
    const prev = prevPosRef.current;
    if (prev) {
      const dx = x - prev.x, dy = y - prev.y;
      if (Math.sqrt(dx * dx + dy * dy) >= TRAIL_MIN_DIST) {
        ptsRef.current.push({ x, y, born: now });
        if (ptsRef.current.length > TRAIL_MAX_PTS)
          ptsRef.current = ptsRef.current.slice(-TRAIL_MAX_PTS);
      }
    }
    prevPosRef.current = { x, y };
  }, []);

  const onMouseLeave = useCallback(() => { prevPosRef.current = null; }, []);

  return { onMouseMove, onMouseLeave };
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function HeroSection() {
  const { resolvedTheme } = useTheme();
  const { t } = useTranslation();
  const isLight = resolvedTheme === "light";

  const [liveProject, setLiveProject] = useState<LiveProject | null>(null);
  const [wordIndex,   setWordIndex]   = useState(0);
  const [wordVisible, setWordVisible] = useState(true);
  const [termIndex,   setTermIndex]   = useState(0);
  const [termVisible, setTermVisible] = useState(true);
  const [sliderIndex, setSliderIndex] = useState(0);

  const sliderImages = [
    convertDriveLink("https://drive.google.com/file/d/1jVeFivA-OERzXuIPyJAnRmlGACORr2FC/view?usp=sharing"),
    convertDriveLink("https://drive.google.com/file/d/1jVeFivA-OERzXuIPyJAnRmlGACORr2FC/view?usp=sharing"),
    convertDriveLink("https://drive.google.com/file/d/1VKrLnz2fIFYQM1Kn6lpFO583-PTmzM4u/view?usp=sharing"),
    convertDriveLink("https://drive.google.com/file/d/1Qxf3XlMwZoXjvL8SgNFbIcxaGZceUx5z/view?usp=sharing"),
    convertDriveLink("https://drive.google.com/file/d/1wO8ewsliSaoAfhFO_5P-4FwvedwihX-Z/view?usp=sharing"),
    convertDriveLink("https://drive.google.com/file/d/15S9tc2AEA-iy2Y3YeYqz_1wHupfZzF0A/view?usp=sharing"),
    convertDriveLink("https://drive.google.com/file/d/1pUlxC_FGVn71NJ77a6HzVwaSHOfd2v96/view?usp=sharing"),
    convertDriveLink("https://drive.google.com/file/d/1ztT7zNKQmHVgRnoQFgtx34ia_9TcliF4/view?usp=sharing"),
    convertDriveLink("https://drive.google.com/file/d/19WnRJ9M_eNUrwfR3ag0Zy596SPGq9yP2/view?usp=sharing"),
    convertDriveLink("https://drive.google.com/file/d/1LH82tJPyANo-MVR8Sttt1mLBsiOF05bc/view?usp=sharing"),
    convertDriveLink("https://drive.google.com/file/d/10u-ndFoCI8_8t1qUEkCFf5BpXh8xLpD_/view?usp=sharing"),
    convertDriveLink("https://drive.google.com/file/d/19JGFU5hJ6rCc-i2HeywYCMk8uaQoSmqg/view?usp=sharing"),
    convertDriveLink("https://drive.google.com/file/d/10aHZ94Y0dg144HH7YsTizZY5qqpiAV2f/view?usp=sharing"),
    convertDriveLink("https://drive.google.com/file/d/1SZtmGfp8znsYCvd-LQT-xp22tw_wFJvN/view?usp=sharing"),
  ];

  const sectionRef   = useRef<HTMLElement>(null);
  const trailCanvas  = useRef<HTMLCanvasElement>(null);
  const [canvasMouse, setCanvasMouse] = useState({ x: -1000, y: -1000 });
  const [parallax,    setParallax]    = useState({ x: 0, y: 0 });

  const { onMouseMove: trailMove, onMouseLeave: trailLeave } = useGhostTrail(trailCanvas, isLight);

  useEffect(() => {
    api.get("/projects/public")
      .then(res => { 
        const d = res.data.data || []; 
        if (d.length) {
          // Daily rotation: use day of year to select project deterministically
          const now = new Date();
          const start = new Date(now.getFullYear(), 0, 0);
          const diff = now.getTime() - start.getTime();
          const oneDay = 1000 * 60 * 60 * 24;
          const dayOfYear = Math.floor(diff / oneDay);
          const projectIndex = dayOfYear % d.length;
          setLiveProject(d[projectIndex]);
        }
      })
      .catch(() => setLiveProject(null));
  }, []);

  useEffect(() => {
    const iv = setInterval(() => {
      setWordVisible(false);
      setTimeout(() => { setWordIndex(i => (i + 1) % rotatingWords.length); setWordVisible(true); }, 350);
    }, 2800);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    const iv = setInterval(() => {
      setTermVisible(false);
      setTimeout(() => { setTermIndex(i => (i + 1) % terminalLines.length); setTermVisible(true); }, 400);
    }, 2600);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    const iv = setInterval(() => {
      setSliderIndex(i => (i + 1) % sliderImages.length);
    }, 5000);
    return () => clearInterval(iv);
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    const s = sectionRef.current;
    if (!s) return;
    const r = s.getBoundingClientRect();
    const x = e.clientX - r.left, y = e.clientY - r.top;
    setCanvasMouse({ x, y });
    setParallax({ x: ((x - r.width / 2) / (r.width / 2)) * 20, y: ((y - r.height / 2) / (r.height / 2)) * 13 });
    trailMove(x, y);
  }, [trailMove]);

  const handleMouseLeave = useCallback(() => {
    setCanvasMouse({ x: -1000, y: -1000 });
    setParallax({ x: 0, y: 0 });
    trailLeave();
  }, [trailLeave]);

  useEffect(() => {
    const s = sectionRef.current;
    if (!s) return;
    s.addEventListener("mousemove", handleMouseMove as EventListener);
    s.addEventListener("mouseleave", handleMouseLeave);
    return () => { s.removeEventListener("mousemove", handleMouseMove as EventListener); s.removeEventListener("mouseleave", handleMouseLeave); };
  }, [handleMouseMove, handleMouseLeave]);

  const handleScrollToServices = () => { const el = document.getElementById("services"); if (el) el.scrollIntoView({ behavior: "smooth" }); };

  const pxLayer = (f: number): React.CSSProperties => ({
    transform: `translate(${-parallax.x * f}px, ${-parallax.y * f}px)`,
    transition: `transform ${0.08 + f * 0.08}s ease-out`,
    willChange: "transform",
  });

  const orbGlow  = isLight ? { background: "rgba(27,110,243,0.06)"  } : { background: "rgba(0,229,255,0.06)"  };
  const orbGlow2 = isLight ? { background: "rgba(99,102,241,0.05)"  } : { background: "rgba(27,110,243,0.08)" };
  const thumbnailUrl = liveProject?.thumbnail_url ? convertDriveLink(liveProject.thumbnail_url) : "";
  const currentTerm  = terminalLines[termIndex];

  return (
    <section
      ref={sectionRef}
      data-component="hero"
      className="relative min-h-screen flex flex-col overflow-hidden"
      style={{ background: isLight ? "linear-gradient(135deg,#EEF4FF 0%,#F4F0FF 55%,#E8F4FF 100%)" : "#000000", cursor:"none" }}
    >
      <style>{`
        /* ── Spin keyframes ── */
        @keyframes spin-ltr  { from{transform:rotateY(0deg)}   to{transform:rotateY(360deg)}  }
        @keyframes spin-cw   { from{transform:rotate(0deg)}    to{transform:rotate(360deg)}   }
        @keyframes spin-ccw  { from{transform:rotate(360deg)}  to{transform:rotate(0deg)}     }
        @keyframes counter-spin-ccw {
          from{transform:translateX(-50%) rotate(0deg)}
          to  {transform:translateX(-50%) rotate(-360deg)}
        }
        /* ── Terminal cursor blink ── */
        @keyframes term-blink { 0%,100%{opacity:1} 50%{opacity:0} }

        /* ── Custom cursor ping ── */
        @keyframes cursor-ping {
          0%  { transform:translate(-50%,-50%) scale(1);   opacity:0.7; }
          100%{ transform:translate(-50%,-50%) scale(2.6); opacity:0;   }
        }

        /* Custom cursor dot — fixed over the whole viewport */
        .hero-cursor {
          position:fixed; pointer-events:none; z-index:9999;
          width:10px; height:10px; border-radius:50%;
          transform:translate(-50%,-50%);
        }

        .logo-spin-ltr { animation:spin-ltr 5s linear infinite; will-change:transform; transform-style:preserve-3d; }
        .ring-cw       { animation:spin-cw  22s linear infinite; }
        .ring-ccw      { animation:spin-ccw 18s linear infinite; }
        .cursor-blink  { animation:term-blink 1s step-end infinite; }

        /* Orbiting logo stays upright by counter-spinning */
        .orbit-logo-upright {
          animation:counter-spin-ccw 22s linear infinite;
          position:absolute; top:-34px; left:50%;
          transform:translateX(-50%);
        }

        /* Trail canvas — sits above particles, below content */
        .trail-canvas {
          position:absolute; inset:0; z-index:3;
          pointer-events:none; width:100%; height:100%;
        }

        /* Orbital responsive scale */
        .orbital-root { --os:1; }
        @media(max-width:380px)                        {.orbital-root{--os:0.58}}
        @media(min-width:381px) and (max-width:520px)  {.orbital-root{--os:0.68}}
        @media(min-width:521px) and (max-width:767px)  {.orbital-root{--os:0.80}}
        @media(min-width:768px) and (max-width:1023px) {.orbital-root{--os:0.90}}
        @media(min-width:1024px)                       {.orbital-root{--os:1.00}}

        /*
          Terminal card is positioned OUTSIDE the 340px canvas (above it),
          anchored to the outer ring. We float it as a regular block above
          the orbital bounding box — no overlap with the logo.
        */
        .terminal-card {
          width: 192px;
          border-radius: 12px;
          padding: 10px 12px 10px 12px;
          font-family: 'Courier New', Courier, monospace;
          overflow: hidden;
          flex-shrink: 0;
        }
      `}</style>

      {/* ── Custom cursor dot ──────────────────────────────── */}
      <CursorDot isLight={isLight} sectionRef={sectionRef} />

      {/* ── Layer 0: Particles ─────────────────────────────── */}
      <div className="absolute inset-0 z-0">
        <ParticleCanvas
          mouseX={canvasMouse.x} mouseY={canvasMouse.y}
          color={isLight ? "#1B6EF3" : "#00E5FF"}
          particleCount={62} connectionDistance={130} repelRadius={120} speed={0.38}
        />
      </div>

      {/* ── Layer 1: Colour mesh ───────────────────────────── */}
      <div className="absolute inset-0 z-[1] pointer-events-none" style={{
        background: isLight
          ? "radial-gradient(ellipse 80% 60% at 25% 30%,rgba(27,110,243,0.07) 0%,transparent 60%),radial-gradient(ellipse 60% 80% at 80% 65%,rgba(99,102,241,0.06) 0%,transparent 60%)"
          : "radial-gradient(ellipse 80% 60% at 40% 20%,rgba(0,229,255,0.07) 0%,transparent 55%),radial-gradient(ellipse 60% 80% at 80% 70%,rgba(27,110,243,0.09) 0%,transparent 60%)",
      }} />

      {/* ── Layer 2: Circuit ───────────────────────────────── */}
      <div className="absolute inset-0 z-[1] bg-circuit-pattern opacity-35 pointer-events-none" />

      {/* ── Layer 3: Parallax orbs ─────────────────────────── */}
      <div className="absolute inset-0 z-[2] pointer-events-none overflow-hidden" style={pxLayer(0.3)}>
        <div className="absolute top-1/4 right-1/4 w-[520px] h-[520px] rounded-full blur-3xl" style={orbGlow} />
        <div className="absolute bottom-1/3 left-1/5 w-72 h-72 rounded-full blur-3xl animate-float" style={{ ...orbGlow2, animationDelay:"2s" }} />
        <div className="absolute top-2/3 right-1/3 w-48 h-48 rounded-full blur-2xl animate-float" style={{ ...orbGlow, animationDelay:"4s" }} />
      </div>

      {/* ── Layer 4: Ghost trail canvas ────────────────────── */}
      <canvas ref={trailCanvas} className="trail-canvas" aria-hidden="true" />

      {/* ── Main content ───────────────────────────────────── */}
      <div className="relative z-10 flex-1 flex items-center w-full px-4 md:px-12 lg:px-20 pt-28 pb-12">
        <div className="w-full max-w-7xl mx-auto flex flex-col lg:grid lg:grid-cols-2 gap-12 lg:gap-16 items-center lg:items-start">

          {/* ════ LEFT COLUMN ════ */}
          {/* Slight upward nudge on desktop to align eyebrow badge with terminal card top */}
          <div style={pxLayer(0.8)} className="lg:-mt-3">
            

            <h1 className="font-black leading-[1.05] mb-6"
              style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:"clamp(2.5rem,5vw,4.5rem)", color: isLight ? "#0A1628" : "#ffffff" }}>
              {t("hero.we_build")}<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-electric-blue via-cyan-400 to-cyber-blue"
                style={{ display:"inline-block", opacity: wordVisible ? 1 : 0, transform: wordVisible ? "translateY(0)" : "translateY(10px)", transition:"opacity 0.35s ease,transform 0.35s ease" }}>
                {rotatingWords[wordIndex]}
              </span><br />
              <span style={{ color: isLight ? "#0A1628" : "#ffffff" }}>{t("hero.that_scale")}.</span>
            </h1>

            <p className="text-silver text-base md:text-lg leading-relaxed mb-10 max-w-lg">
              {t("hero.description")}
            </p>

            <div className="flex flex-wrap items-center gap-4 mb-12">
              <Link to="/contact" className="flex items-center gap-2.5 bg-electric-blue text-deep-black font-bold px-7 py-3.5 rounded-full hover:shadow-glow-blue transition-all duration-300 whitespace-nowrap cursor-pointer text-sm">
                {t("hero.cta_primary")} <i className="ri-arrow-right-line" />
              </Link>
              <Link to="/portfolio" className="flex items-center gap-2 text-silver hover:text-white transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm group">
                <span className="w-8 h-8 flex items-center justify-center rounded-full border border-dark-border group-hover:border-electric-blue/40 transition-colors">
                  <i className="ri-play-line text-xs" />
                </span>
                {t("hero.cta_secondary")}
              </Link>
            </div>

            <div className="flex flex-wrap gap-8">
              {[{value:"18+",label:t("hero.stats_projects")},{value:"88%",label:t("hero.stats_satisfaction")},{value:"10+",label:t("hero.stats_engineers")},{value:"02yrs",label:t("hero.stats_experience")}].map(s => (
                <div key={s.label}>
                  <div className="text-2xl font-black text-electric-blue" style={{ fontFamily:"'Space Grotesk',sans-serif" }}>{s.value}</div>
                  <div className="text-silver-dim text-xs mt-0.5 uppercase tracking-widest">{s.label}</div>
                </div>
              ))}
            </div>

            {/* ── Image Slider with PDF Download ───────────── */}
            <div className="relative w-full max-w-2xl rounded-2xl overflow-hidden group mt-8">
              <div className="relative aspect-video">
                {sliderImages.map((img, idx) => (
                  <img
                    key={idx}
                    src={img}
                    alt={`Slide ${idx + 1}`}
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
                      idx === sliderIndex ? "opacity-100" : "opacity-0"
                    }`}
                  />
                ))}
                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              </div>

              {/* Slider indicators */}
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
                {sliderImages.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSliderIndex(idx)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      idx === sliderIndex ? "bg-electric-blue w-6" : "bg-white/40 hover:bg-white/60"
                    }`}
                  />
                ))}
              </div>

              {/* PDF Download Button - appears on hover */}
              <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <a
                  href="/company-profile.pdf"
                  download
                  className="flex items-center gap-2 bg-electric-blue text-deep-black text-xs font-bold px-4 py-2 rounded-full hover:shadow-glow-blue transition-all duration-300 cursor-pointer"
                >
                  <i className="ri-file-pdf-line" />
                  Download PDF
                </a>
              </div>
            </div>
          </div>

          {/* ════ RIGHT COLUMN — terminal card + orbital + metric cards + live project ════ */}
          <div className="orbital-root relative flex flex-col items-center gap-5 w-full" style={pxLayer(0.5)}>

            {/* ══════════════════════════════════════════════════════════════
                TERMINAL CARD — sits ABOVE the orbital, on the outer ring plane.
                Rendered as a normal block so it never overlaps the logo.
                Has macOS traffic lights + cycling bash prompt + blinking cursor.
            ══════════════════════════════════════════════════════════════ */}
            <div
              className="terminal-card"
              style={{
                border: `1px solid ${isLight ? "rgba(27,110,243,0.22)" : "rgba(0,229,255,0.18)"}`,
                background: isLight ? "rgba(240,246,255,0.95)" : "rgba(4,10,22,0.92)",
                boxShadow: isLight
                  ? "0 4px 24px rgba(27,110,243,0.10), inset 0 1px 0 rgba(255,255,255,0.7)"
                  : "0 4px 24px rgba(0,229,255,0.07), inset 0 1px 0 rgba(255,255,255,0.04)",
              }}
            >
              {/* Traffic lights */}
              <div style={{ display:"flex", gap:5, marginBottom:8 }}>
                <span style={{ width:9, height:9, borderRadius:"50%", background:"#ff5f56", display:"block" }} />
                <span style={{ width:9, height:9, borderRadius:"50%", background:"#ffbd2e", display:"block" }} />
                <span style={{ width:9, height:9, borderRadius:"50%", background:"#27c93f", display:"block" }} />
              </div>

              {/* Command line */}
              <div style={{
                fontSize:10, lineHeight:1.5,
                color: isLight ? "#1B6EF3" : "#00E5FF",
                opacity: termVisible ? 1 : 0,
                transform: termVisible ? "translateY(0)" : "translateY(3px)",
                transition:"opacity 0.35s ease,transform 0.35s ease",
                whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis",
              }}>
                {currentTerm.cmd}
              </div>

              {/* Output line */}
              <div style={{
                fontSize:9.5, lineHeight:1.5,
                color: isLight ? "#15803d" : "#27c93f",
                opacity: termVisible ? 1 : 0,
                transform: termVisible ? "translateY(0)" : "translateY(3px)",
                transition:"opacity 0.4s ease 0.07s,transform 0.4s ease 0.07s",
                whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis",
                marginBottom:6,
              }}>
                {currentTerm.out}
              </div>

              {/* Blinking cursor row */}
              <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                <span style={{ fontSize:10, color: isLight ? "#1B6EF3" : "#00E5FF" }}>$</span>
                <span className="cursor-blink" style={{
                  display:"inline-block", width:6, height:11,
                  background: isLight ? "#1B6EF3" : "#00E5FF",
                  borderRadius:1, verticalAlign:"middle",
                }} />
              </div>
            </div>

            {/* ══════════════════════════════════════════════════════════════
                ORBITAL SYSTEM — 340×340 px canvas, scaled via --os
            ══════════════════════════════════════════════════════════════ */}
            <div style={{ width:"calc(340px * var(--os,1))", height:"calc(340px * var(--os,1))", position:"relative", flexShrink:0 }}>
              <div style={{ position:"absolute", top:"50%", left:"50%", width:340, height:340, transform:"translate(-50%,-50%) scale(var(--os,1))", transformOrigin:"center center" }}>

                {/* Outer dashed ring CCW */}
                <div className="ring-ccw" style={{ position:"absolute", top:0, left:0, width:340, height:340, borderRadius:"50%", border:"1px dashed rgba(27,110,243,0.22)" }}>
                  <div style={{ position:"absolute", bottom:8, right:8, width:9, height:9, borderRadius:"50%", background:"rgba(27,110,243,0.85)", boxShadow:"0 0 10px rgba(27,110,243,0.7)" }} />
                  <div style={{ position:"absolute", top:"25%", left:0, width:7, height:7, borderRadius:"50%", background:"rgba(0,229,255,0.65)" }} />
                  <div style={{ position:"absolute", top:"60%", right:"5%", width:5, height:5, borderRadius:"50%", background:"rgba(99,102,241,0.5)" }} />
                </div>

                {/* Middle ring CW + orbiting logo */}
                <div className="ring-cw" style={{ position:"absolute", top:30, left:30, width:280, height:280, borderRadius:"50%", border:"1px solid rgba(27,110,243,0.18)" }}>
                  <div className="orbit-logo-upright">
                    <img src={LOGO_SRC} alt="Orbiting Logo"
                      style={{ width:68, height:68, objectFit:"contain", display:"block",
                        filter: isLight ? "none" : "drop-shadow(0 0 6px rgba(0,229,255,0.4))" }} />
                  </div>
                </div>

                {/* Inner pulse ring */}
                <div className="animate-pulse-ring" style={{ position:"absolute", top:75, left:75, width:190, height:190, borderRadius:"50%", border:"1px solid rgba(27,110,243,0.22)" }} />

                {/* Ambient inner glow */}
                <div style={{ position:"absolute", top:105, left:105, width:130, height:130, borderRadius:"50%", filter:"blur(32px)", pointerEvents:"none",
                  ...(isLight ? { background:"rgba(27,110,243,0.13)" } : { background:"rgba(0,229,255,0.09)" }) }} />

                {/* ── CENTER LOGO — rotateY left-to-right ── */}
                <div style={{
                  position:"absolute", top:"50%", left:"50%",
                  transform:"translate(-50%,-50%)",
                  width:148, height:148, borderRadius:"50%",
                  border:"1px solid rgba(27,110,243,0.28)",
                  backdropFilter:"blur(14px)",
                  background: isLight ? "rgba(255,255,255,0.78)" : "rgba(10,22,40,0.72)",
                  boxShadow: isLight
                    ? "0 0 36px rgba(27,110,243,0.14),inset 0 1px 0 rgba(255,255,255,0.6)"
                    : "0 0 36px rgba(0,229,255,0.11),inset 0 1px 0 rgba(255,255,255,0.06)",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  zIndex:10, perspective:600,
                }}>
                  <img src={LOGO_SRC} alt="Schemackozo" className="logo-spin-ltr"
                    style={{ width:112, height:112, objectFit:"contain",
                      filter: isLight ? "none" : "drop-shadow(0 0 8px rgba(0,229,255,0.3))" }}
                    aria-hidden="true" />
                </div>

                {/* "Live Now" badge — right side of orbital */}
                <div style={{ position:"absolute", right:-14, top:"50%", transform:"translateY(-50%)",
                  display:"flex", alignItems:"center", gap:6, padding:"6px 13px",
                  borderRadius:999, border:"1px solid rgba(39,201,63,0.28)",
                  backdropFilter:"blur(10px)",
                  background: isLight ? "rgba(255,255,255,0.92)" : "rgba(13,27,46,0.88)",
                  zIndex:20 }}>
                  <span style={{ width:7, height:7, borderRadius:"50%", background:"#27c93f", boxShadow:"0 0 7px #27c93f", flexShrink:0 }} className="animate-glow-pulse" />
                  <span style={{ fontSize:10, fontWeight:700, color: isLight ? "#0A1628" : "#e2e8f0", whiteSpace:"nowrap" }}>Deployment</span>
                </div>

                {/* "Building" badge — left side of orbital */}
                <div style={{ position:"absolute", left:-18, top:"25%",
                  display:"flex", alignItems:"center", gap:6, padding:"6px 11px",
                  borderRadius:999, border:"1px solid rgba(27,110,243,0.22)",
                  backdropFilter:"blur(10px)",
                  background: isLight ? "rgba(255,255,255,0.92)" : "rgba(13,27,46,0.88)",
                  zIndex:20 }}>
                  <i className="ri-code-s-slash-line" style={{ color:"#1B6EF3", fontSize:12, flexShrink:0 }} />
                  <span style={{ fontSize:10, fontWeight:700, color: isLight ? "#0A1628" : "#e2e8f0", whiteSpace:"nowrap" }}>Development</span>
                </div>

              </div>
            </div>{/* end orbital bounding box */}

            {/* ── METRIC CARDS ───────────────────────────── */}
            <div className="grid grid-cols-2 gap-4 w-full max-w-[360px]">
              {[
                { icon:"ri-rocket-line", iconCol:"#27c93f", bgCol:"rgba(39,201,63,0.08)", borderCol:"rgba(39,201,63,0.2)", val:"10+", label:t("hero.metric_products") },
                { icon:"ri-global-line", iconCol:"#1B6EF3", bgCol:"rgba(27,110,243,0.08)", borderCol:"rgba(27,110,243,0.2)", val:"03+",  label:t("hero.metric_countries") },
              ].map(card => (
                <div key={card.label}
                  className="border border-dark-border rounded-xl p-4 backdrop-blur-sm hover:border-electric-blue/30 transition-colors duration-200"
                  style={{ background: isLight ? "rgba(255,255,255,0.78)" : "rgba(13,27,46,0.72)" }}>
                  <div style={{ width:36, height:36, borderRadius:8, background:card.bgCol, border:`1px solid ${card.borderCol}`, display:"flex", alignItems:"center", justifyContent:"center", marginBottom:12 }}>
                    <i className={card.icon} style={{ color:card.iconCol, fontSize:16 }} />
                  </div>
                  <div className="text-2xl font-black mb-0.5" style={{ fontFamily:"'Space Grotesk',sans-serif", color: isLight ? "#0A1628" : "#ffffff" }}>{card.val}</div>
                  <div className="text-silver-dim text-xs">{card.label}</div>
                </div>
              ))}
            </div>

            {/* ── LIVE PROJECT CARD ──────────────────────── */}
            {liveProject ? (
              <Link
                to={`/projects/${liveProject.id}`}
                className="block w-full max-w-[360px] group"
              >
                <div
                  className="border border-dark-border rounded-2xl overflow-hidden backdrop-blur-sm transition-all duration-300 group-hover:border-electric-blue/40 group-hover:shadow-lg"
                  style={{ background: isLight ? "rgba(255,255,255,0.78)" : "rgba(13,27,46,0.72)" }}
                >
                  {thumbnailUrl && (
                    <div style={{ width:"100%", height:148, overflow:"hidden", flexShrink:0 }}>
                      <img src={thumbnailUrl} alt={liveProject.title}
                        style={{ width:"100%", height:"100%", objectFit:"cover", display:"block", transition:"transform 0.4s ease" }}
                        className="group-hover:scale-105"
                        onError={e => { const p = e.currentTarget.parentElement as HTMLElement|null; if(p) p.style.display="none"; }} />
                    </div>
                  )}
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-cyber-green animate-glow-pulse flex-shrink-0" />
                        <span className="text-silver-dim text-xs">{t("hero.live_project")}</span>
                      </div>
                      <span className="text-electric-blue text-xs font-semibold border border-electric-blue/20 px-2.5 py-1 rounded-full bg-electric-blue/5 whitespace-nowrap">
                        {liveProject.stage}
                      </span>
                    </div>
                    <h3 className="font-bold text-sm mb-1 line-clamp-1"
                      style={{ fontFamily:"'Space Grotesk',sans-serif", color: isLight ? "#0A1628" : "#ffffff" }}>
                      {liveProject.title}
                    </h3>
                    <p className="text-silver-dim text-xs mb-3 line-clamp-2">{liveProject.description || "Ongoing development"}</p>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: isLight ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.08)" }}>
                        <div className="h-full rounded-full" style={{ width:`${liveProject.progress||0}%`, background:"linear-gradient(90deg,#1B6EF3,#00E5FF)", transition:"width 0.7s ease" }} />
                      </div>
                      <span className="text-electric-blue text-xs font-bold tabular-nums">{liveProject.progress||0}%</span>
                    </div>
                  </div>
                </div>
              </Link>
            ) : (
              <div className="w-full max-w-[360px] border border-electric-blue/15 rounded-2xl p-5 backdrop-blur-sm"
                style={{ background: isLight ? "rgba(238,244,255,0.82)" : "rgba(0,229,255,0.03)" }}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 rounded-full bg-silver-dim" />
                  <span className="text-silver-dim text-xs">{t("hero.no_active_project")}</span>
                </div>
                <p className="text-silver-dim text-xs">{t("hero.no_active_project_desc")}</p>
              </div>
            )}

            <div className="absolute -inset-8 rounded-3xl blur-2xl -z-10 pointer-events-none"
              style={isLight ? { background:"rgba(27,110,243,0.04)" } : { background:"rgba(0,229,255,0.03)" }} />
          </div>

        </div>
      </div>

      {/* ── Scroll indicator ── */}
      <div className="relative z-10 flex justify-center pb-8">
        <button onClick={handleScrollToServices}
          className="flex flex-col items-center gap-2 text-silver-dim hover:text-electric-blue transition-colors cursor-pointer group"
          aria-label="Scroll to services">
          <span className="text-xs uppercase tracking-[0.2em]">{t("hero.scroll")}</span>
          <div className="w-5 h-8 border border-silver-dim/30 rounded-full flex items-start justify-center pt-1.5 group-hover:border-electric-blue/50 transition-colors">
            <div className="w-1 h-2 bg-electric-blue rounded-full animate-bounce" />
          </div>
        </button>
      </div>

      {/* ── Tech Marquee ── */}
      <div className="relative z-10 border-t border-dark-border backdrop-blur-sm py-3.5 overflow-hidden"
        style={{ background: isLight ? "rgba(245,248,255,0.7)" : "rgba(10,22,40,0.6)" }}>
        <div className="flex animate-marquee whitespace-nowrap">
          {techBadges.map((tech, i) => (
            <span key={i} className="inline-flex items-center gap-2 mx-8 text-silver-dim text-xs font-medium uppercase tracking-widest">
              <span className="w-1 h-1 rounded-full bg-electric-blue/50" />
              {tech}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}



// ─────────────────────────────────────────────────────────────────────────────
// CURSOR DOT — glowing dot + expanding ring that replaces the browser cursor
// inside the hero section only.
// ─────────────────────────────────────────────────────────────────────────────
function CursorDot({
  isLight,
  sectionRef,
}: {
  isLight: boolean;
  sectionRef: React.RefObject<HTMLElement>;
}) {
  const dotRef = useRef<HTMLDivElement>(null);
  const [inside, setInside] = useState(false);

  useEffect(() => {
    const dot = dotRef.current;
    if (!dot) return;
    const move  = (e: MouseEvent) => { dot.style.left = `${e.clientX}px`; dot.style.top = `${e.clientY}px`; };
    const enter = () => setInside(true);
    const leave = () => setInside(false);
    const s = sectionRef.current;
    window.addEventListener("mousemove", move, { passive: true });
    s?.addEventListener("mouseenter", enter);
    s?.addEventListener("mouseleave", leave);
    return () => {
      window.removeEventListener("mousemove", move);
      s?.removeEventListener("mouseenter", enter);
      s?.removeEventListener("mouseleave", leave);
    };
  }, [sectionRef]);

  const col = isLight ? "#1B6EF3" : "#00E5FF";

  return (
    <div
      ref={dotRef}
      className="hero-cursor"
      style={{ opacity: inside ? 1 : 0, transition: "opacity 0.2s ease" }}
    >
      {/* Core dot */}
      <div style={{
        width: 8, height: 8, borderRadius: "50%",
        background: col,
        boxShadow: `0 0 10px ${col}, 0 0 20px ${col}55`,
        position: "absolute", top: "50%", left: "50%",
        transform: "translate(-50%,-50%)",
      }} />
      {/* Expanding ping ring */}
      <div style={{
        position: "absolute", top: "50%", left: "50%",
        width: 24, height: 24, borderRadius: "50%",
        border: `1.5px solid ${col}`,
        transform: "translate(-50%,-50%)",
        opacity: 0.45,
        animation: "cursor-ping 1.4s ease-out infinite",
      }} />
    </div>
  );
}